import { addTenant, deleteTenant, useTenants } from '@api/tenants';
import { DataGrid } from '@components/DataGrid';
import { ApprovalDialog, DeleteConfirmationDialog } from '@components/Forms';
import { Box, styled } from '@mui/system';
import { GridRowParams } from '@mui/x-data-grid';
import { getTenantsColumns } from '@utils/tables';
import { TENANTS_FIELDS } from 'constants/global';
import { APP_ROUTES } from 'constants/routes';
import { useNavigate, useToast } from 'hooks';
import { noop } from 'lodash';
import React from 'react';
import { TenantStatus } from 'types/common';
import { ITenantCreationFields } from 'types/settings';
import { ITenant } from 'types/tenants';
import { ToastSeverity } from 'types/toast';
import TenantCreateButton from './TenantCreateButton';

const TenantsDataGrid = styled(DataGrid)`
	.tenant-table-row {
		cursor: pointer;
	}
`;

const VISIBILITY_MODEL = {
	[TENANTS_FIELDS.version]: false
};

interface IActionStatus {
	open: boolean;
	id: string;
	status?: TenantStatus;
}

const TenantsTable: React.FC = () => {
	const [deleteTenantConfirmation, setDeleteTenantConfirmation] =
		React.useState({
			open: false,
			id: '',
			name: ''
		});
	const [statusAction] = React.useState<IActionStatus>({
		open: false,
		id: '',
		status: TenantStatus.disabled
	});
	const { data, isLoading, refetch } = useTenants();

	const { addToast } = useToast();
	const navigate = useNavigate();

	const handleNavigateToTenant = React.useCallback(
		(params: GridRowParams) => {
			navigate(`${APP_ROUTES.tenants}/${encodeURIComponent(params.id)}`);
		},
		[navigate]
	);

	const handleApproveAddTenant = React.useCallback(
		async (newTenant: ITenantCreationFields) => {
			try {
				const res = await addTenant(newTenant);
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr('tenants.creation.save.dialog.approve')
				});
				await refetch();
				return res;
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('tenants.creation.save.dialog.failed'),
					error
				});
			}
			return undefined;
		},
		[addToast, refetch]
	);

	const onDeleteActionClicked = React.useCallback(
		(tenant: ITenant) => {
			setDeleteTenantConfirmation({
				open: true,
				id: tenant.id,
				name: tenant.name
			});
		},
		[setDeleteTenantConfirmation]
	);

	const handleDeleteConfirmation = React.useCallback(async () => {
		try {
			await deleteTenant(deleteTenantConfirmation.id);
			setDeleteTenantConfirmation({
				open: false,
				id: '',
				name: ''
			});
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('tenants.delete.dialog.approve')
			});
			await refetch();
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('tenants.delete.dialog.failed'),
				error
			});
		}
	}, [
		deleteTenantConfirmation,
		setDeleteTenantConfirmation,
		refetch,
		addToast
	]);

	const handleCancelConfirmation = React.useCallback(() => {
		setDeleteTenantConfirmation({
			open: false,
			id: '',
			name: ''
		});
	}, [setDeleteTenantConfirmation]);

	const tenantsColumns = React.useMemo(
		() => getTenantsColumns(onDeleteActionClicked),
		[onDeleteActionClicked]
	);

	return (
		<Box display="flex" flexGrow={1} data-testid="tenants-table">
			{deleteTenantConfirmation.open && (
				<DeleteConfirmationDialog
					onDelete={handleDeleteConfirmation}
					onCancel={handleCancelConfirmation}
					name={deleteTenantConfirmation.name}
					title={intl.tr('tenants.delete.dialog.title')}
					question={intl.tr('tenants.delete.dialog.question', {
						tenantName: deleteTenantConfirmation.name
					})}
					extraVerification
					inputHeader={intl.tr('tenants.delete.dialog.input.header.label')}
					inputText={intl.tr('tenants.delete.dialog.input.name.label')}
				/>
			)}
			{statusAction.open && (
				<ApprovalDialog
					title={intl.tr(
						`tenants.table.actions.status.dialog.${statusAction.status}`
					)}
					onCancel={noop}
					question=""
					onApprove={noop}
					color="primary"
					variant="contained"
					cancelText={intl.tr('button.cancel')}
					approveText={intl.tr(
						statusAction.status === TenantStatus.active
							? 'button.disable'
							: 'button.enable'
					)}
				/>
			)}
			<TenantsDataGrid
				columnVisibilityModel={VISIBILITY_MODEL}
				rows={data?.tenants || []}
				columns={tenantsColumns}
				loading={isLoading}
				actions={<TenantCreateButton onApprove={handleApproveAddTenant} />}
				onRowClick={handleNavigateToTenant}
				getRowClassName={() => 'tenant-table-row'}
				noDataMessageId="grid.data.no.data.tenants"
				checkboxSelection={false}
				initialState={{
					sorting: {
						sortModel: [{ field: TENANTS_FIELDS.createdTime, sort: 'desc' }]
					}
				}}
			/>
		</Box>
	);
};

export default TenantsTable;
